import React, { useState, useEffect } from "react";
import {
  Box,
  Autocomplete,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Typography,
  Grid2 as Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { useFormik } from "formik";
import {
  editAssetName,
  getFileTypes,
  getProjectName,
  uploadFile,
  getDbConnectionData,
  generatePdf,
} from "../services/BackendServices";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../redux/Hook";
import {
  ASSET_NAME_UPDATE_ERROR,
  ASSET_NAME_UPDATE_SUCCESS,
  COBOL,
  SAS,
} from "./Constants";
import "./Styles.css";
import {
  DATABASE,
  DATABASE_CONNECTED,
  ERROR_FETCH_DATA,
  ERROR_FILE_TYPES,
  FALIED_PDF,
  FILE_REQUIRED,
  FILE_UPLOADED,
  PDF_GENERATED,
  UNEXPECTED_ERROR,
  UPLOAD_FAILED,
} from "../constants/constants";

type CreateKnowledgeProps = {
  getKnowLedgeAssets: () => Promise<void>;
};

const CreateKnowledge: React.FC<CreateKnowledgeProps> = ({
  getKnowLedgeAssets,
}) => {
  const notification = useAppSelector((state) => state.NotificationAlert);
  const dispatch = useDispatch();
  const [etlOption, setEtlOption] = useState<string | null>(null);
  const [fileInput, setFileInput] = useState<File | null>(null);
  const [zipFile, setZipfile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [projectOptions, setProjectOptions] = useState<string[]>([]);
  const [project, setProject] = useState<string | null>(null);
  const [assetName, setAssetName] = useState<string>("");
  const [old_asset_name, setOld] = useState<string>("");
  const [projectName, setProjectName] = useState<string>("");
  const [editStatus, setEditStatus] = useState<string>("");
  const [type, setType] = useState("file");
  const [fileType, setFileType] = useState("");
  const [fileTypes, setFileTypes] = useState<
    { label: string; value: string }[]
  >([]);
  const [fileValues, setFileValues] = useState<any>({});
  const [operation, setoperation] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [databases, setDatabases] = useState<string[]>([]);
  const [tables, setTables] = useState<string[]>([]);
  const [apiResponse, setApiResponse] = useState<any>(null);
  const [selectedAsDatabase, setSelectedAsDatabase] = useState<boolean>(false);

  useEffect(() => {
    if (apiResponse?.data) {
      setDatabases(Object.keys(apiResponse?.data));
    }
  }, [apiResponse]);

  const formik = useFormik({
    initialValues: {
      database: "",
      db: "",
      project_name: "",
      asset_type: "",
      user: "",
      password: "",
      host: "",
      port: "",
      databaseType: "",
      table: "",
      dbTable: [],
    },
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const response = await generatePdf({
          asset_type: values?.asset_type,
          project_name: values?.project_name,
          dbType: values?.databaseType,
          dbHost: values?.host,
          port: values?.port,
          username: values?.user,
          password: values?.password,
          dbName: values?.db,
          tablesList: values?.dbTable?.join(" "),
        });

        if (response?.success) {
          formik?.resetForm();
          dispatch({
            type: "SEND_ALERT",
            data: {
              ...notification,
              enable: true,
              type: "success",
              message: response?.message || PDF_GENERATED,
              duration: 3000,
            },
          });
          await getKnowLedgeAssets();
          setDialogOpen(false);
        } else {
          throw new Error(response?.message || FALIED_PDF);
        }
      } catch (error: any) {
        dispatch({
          type: "SEND_ALERT",
          data: {
            ...notification,
            enable: true,
            type: "error",
            message: error?.message || UNEXPECTED_ERROR,
            duration: 3000,
          },
        });
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    fetchProjectNames();
  }, []);

  useEffect(() => {
    if (project) {
      fetchFileTypes(project);
    } else {
      setFileTypes([]);
    }
  }, [project]);

  const fetchProjectNames = async () => {
    const response = await getProjectName();
    if (response?.success) {
      setProjectOptions(response?.data?.project_names);
    } else {
      setProjectOptions([]);
    }
  };

  const handleDatabaseChange = (event: any) => {
    const selectedDb = event.target.value;
    formik?.setFieldValue("db", selectedDb);
    formik?.setFieldValue("dbTable", "");
    setTables(apiResponse.data[selectedDb] || []);
  };

  const fetchFileTypes = async (project_name: string | null) => {
    if (!project_name) {
      return;
    }
    const response: any = await getFileTypes(project_name);
    try {
      const mappedData = response?.data?.map((item: any) => ({
        label: item,
        value: item,
      }));
      setFileTypes(mappedData);
    } catch (error) {
      console.error(ERROR_FILE_TYPES, error);
      setFileTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditAssetName = async () => {
    setOld(assetName);
    const result = await editAssetName(
      project ?? projectName,
      assetName,
      old_asset_name ? old_asset_name : ""
    );
    setEditStatus(
      result?.success
        ? ASSET_NAME_UPDATE_SUCCESS
        : `${ASSET_NAME_UPDATE_ERROR}: ${result?.message}`
    );
  };

  const handleFolderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event?.target?.files?.[0];
    if (file) {
      setZipfile(file);
      setOld(file?.name);
      setAssetName(file?.name);
      const assetType = "";
      const projectName = project ?? "";
      setFileValues({
        file: file,
        operation: operation,
        name: file?.name,
        type: assetType,
        projectName: projectName,
      });
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event?.target?.files?.[0];
    if (file) {
      setZipfile(file);
      setOld(file.name);
      setAssetName(file?.name);
      const assetType = "";
      const projectName = project ?? "";
      setFileValues({
        file: file,
        operation: operation,
        name: file?.name,
        type: assetType,
        projectName: projectName,
      });
    }
  };

  const handleETLChange = (
    event: React.SyntheticEvent,
    value: { label: string; value: string } | null
  ) => {
    formik?.setFieldValue("asset_type", value?.value);
    setFileType(value?.value ?? "");
    if (value?.value === DATABASE) {
      setSelectedAsDatabase(true);
      setDialogOpen(true);
      setEtlOption(DATABASE);
      return;
    } else {
      setSelectedAsDatabase(false);
    }
    if (value?.value === "cobol") {
      setoperation("process_cobol");
      setType("text/*");
    } else if (value?.value === "sas") {
      setoperation("process_sas");
      setType(".zip");
    }
    fetchFileTypes(project);
    setEtlOption(value ? value.value : null);
    setFileInput(null);
    setZipfile(null);
  };

  const handleFileUpload = async () => {
    setLoading(true);
    try {
      let uploadParams: any = {
        operation,
        assetName: assetName || fileValues?.name,
        type: etlOption,
        projectName: project ?? fileValues?.projectName,
      };
      if (etlOption === DATABASE) {
        Object.assign(uploadParams, {
          dbType: formik?.values?.databaseType,
          username: formik?.values?.user,
          host: formik?.values?.host,
          port: formik?.values?.port,
          password: formik?.values?.password,
        });
      } else {
        if (!fileValues?.file) throw new Error(FILE_REQUIRED);
        uploadParams.file = fileValues.file;
      }
      const response = await uploadFile(uploadParams);
      setApiResponse(response);
      if (response?.success) {
        dispatch({
          type: "SEND_ALERT",
          data: {
            ...notification,
            enable: true,
            type: "success",
            message:
              response.message ||
              (etlOption === DATABASE ? DATABASE_CONNECTED : FILE_UPLOADED),
            duration: 3000,
          },
        });
        await getKnowLedgeAssets();
      } else {
        throw new Error(response?.message || UPLOAD_FAILED);
      }
    } catch (error: any) {
      dispatch({
        type: "SEND_ALERT",
        data: {
          ...notification,
          enable: true,
          type: "error",
          message: error?.message || UNEXPECTED_ERROR,
          duration: 3000,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAsset = () => {
    if (etlOption === DATABASE) {
      formik?.handleSubmit();
    } else {
      handleFileUpload();
    }
  };

  const handleDbTypeSelection = async (name: string) => {
    try {
      formik?.setFieldValue("databaseType", name);
      const response = await getDbConnectionData(name);
      if (response?.success) {
        formik?.setFieldValue("host", response?.data?.data?.host);
        formik?.setFieldValue("port", response?.data?.data?.port);
        formik?.setFieldValue("user", response?.data?.data?.username);
      }
    } catch (error) {
      console.error(ERROR_FETCH_DATA, error);
    }
  };

  const handleDatabaseConnect = () => {
    setFileValues({
      ...fileValues,
      projectName: project,
      dbType: formik?.values?.databaseType,
      username: formik?.values?.user,
      host: formik?.values?.host,
      port: formik?.values?.port,
      password: formik?.values?.password,
    });
    handleFileUpload();
    setDialogOpen(false);
  };

  return (
    <Box className="createKnowledge-test-container">
      <div className="createKnowledge-form-container">
        <div className="createKnowledge-form-container-field">
          <div className="label-textfield-container">
            <div className="field-label">Project Name</div>
            <Autocomplete
              size="small"
              fullWidth
              options={projectOptions ?? []}
              value={project}
              onChange={(_event, newValue) => {
                setProject(newValue);
                formik?.setFieldValue("project_name", newValue);
                fetchFileTypes(projectName);
              }}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" fullWidth />
              )}
            />
          </div>
        </div>
        <div className="label-textfield-container createKnowledge-form-container-field-autocomplete">
          <Box className="field-label">Select Source Type</Box>
          <Box className="label-textfield-container">
            <Autocomplete
              size="small"
              fullWidth
              options={fileTypes ?? []}
              getOptionLabel={(option) => option?.label}
              onChange={handleETLChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  className="test-autocomplete"
                />
              )}
              isOptionEqualToValue={(option, value) =>
                option?.value === value?.value
              }
            />
            {etlOption && etlOption !== DATABASE && !loading && (
              <Button
                variant="contained"
                component="label"
                className="test-button"
                endIcon={<InsertDriveFileIcon />}
              >
                Select File
                <input
                  type={"file"}
                  accept={type}
                  onChange={
                    etlOption === SAS ? handleFolderChange : handleFileChange
                  }
                  className="create-knowledge-select-file"
                />
              </Button>
            )}
          </Box>
        </div>
        {etlOption !== "DATABASE" && (
          <div className="createKnowledge-form-container-field">
            <div className="label-textfield-container">
              <div className="field-label">Asset Name</div>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                value={assetName}
                onChange={(e) => setAssetName(e.target.value)}
              />
            </div>
          </div>
        )}
      </div>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Enter Database Configuration</DialogTitle>
        <DialogContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleDatabaseConnect();
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="body1">Database Type</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl
                  fullWidth
                  margin="dense"
                  error={
                    formik?.touched?.databaseType &&
                    Boolean(formik?.errors?.databaseType)
                  }
                >
                  <Select
                    name="databaseType"
                    value={formik?.values?.databaseType}
                    onChange={(e) => {
                      handleDbTypeSelection(e.target.value);
                    }}
                    onBlur={formik?.handleBlur}
                  >
                    <MenuItem value="mysql">MySQL</MenuItem>
                    <MenuItem value="postgresql">PostgreSQL</MenuItem>
                    <MenuItem value="oracle">Oracle</MenuItem>
                    <MenuItem value="mssql">MS SQL</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="body1">User Name</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  margin="dense"
                  name="user"
                  value={formik?.values?.user}
                  onChange={formik?.handleChange}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="body1">Host Name</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  margin="dense"
                  name="host"
                  value={formik?.values?.host}
                  onChange={formik?.handleChange}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="body1">Port</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  margin="dense"
                  name="port"
                  value={formik?.values?.port}
                  onChange={formik?.handleChange}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="body1">Password</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  margin="dense"
                  type="password"
                  name="password"
                  value={formik?.values?.password}
                  onChange={formik?.handleChange}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleDatabaseConnect}
            color="secondary"
            disabled={
              !formik?.values?.user ||
              !formik?.values?.password ||
              !formik?.values?.host ||
              !formik?.values?.databaseType ||
              !formik?.values?.port ||
              !project
            }
          >
            Connect
          </Button>
        </DialogActions>
      </Dialog>
      {formik?.values?.databaseType && (
        <Box mt={2} className="selected-database-display">
          <Typography variant="body1">
            Connected Database: {formik?.values?.databaseType.toUpperCase()}
          </Typography>
        </Box>
      )}
      {selectedAsDatabase && etlOption == DATABASE ? (
        <Box>
          <div className="label-textfield-container">
            <div className="field-label">Select Database</div>
            <FormControl fullWidth margin="normal">
              <Autocomplete
                size="small"
                options={databases}
                value={formik?.values?.db}
                onChange={(_, newValue) => {
                  formik?.setFieldValue("db", newValue);
                  formik?.setFieldValue("dbTable", "");
                  setTables(newValue ? apiResponse?.data[newValue] : []);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Database"
                    variant="outlined"
                  />
                )}
              />
            </FormControl>
          </div>
          <div className="label-textfield-container">
            <div className="field-label">Select Table</div>
            <FormControl
              fullWidth
              margin="normal"
              disabled={!formik?.values?.db}
            >
              <Autocomplete
                multiple
                size="small"
                options={tables}
                value={formik?.values?.dbTable || []}
                onChange={(_, newValue) =>
                  formik?.setFieldValue("dbTable", newValue)
                }
                disabled={!formik?.values?.db}
                renderInput={(params) => (
                  <TextField
                    placeholder="Select Tables"
                    {...params}
                    variant="outlined"
                  />
                )}
              />
            </FormControl>
          </div>
        </Box>
      ) : null}
      <Button
        variant="contained"
        color="primary"
        onClick={handleCreateAsset}
        className="create-asset-button"
      >
        Create Asset
      </Button>
      <Box mt={3}>{loading && <CircularProgress />}</Box>
    </Box>
  );
};

export default CreateKnowledge;
